import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

// Only English bundled inline as fallback — all other languages loaded on demand
import enTranslations from '@/locales/en.json';

const AVAILABLE_LANGUAGES = [
  'en', 'hu', 'de', 'es', 'pt', 'hi', 'ml', 'te', 'bg', 'cs', 'sk', 'ur', 'pa', 'zh', 'yue'
];

const RTL_LANGUAGES = new Set(['ur']);

// Language file loaders — webpack will code-split each into its own chunk
const languageLoaders = {
  en: () => Promise.resolve(enTranslations),
  hu: () => import('@/locales/hu.json').then(m => m.default),
  de: () => import('@/locales/de.json').then(m => m.default),
  es: () => import('@/locales/es.json').then(m => m.default),
  pt: () => import('@/locales/pt.json').then(m => m.default),
  hi: () => import('@/locales/hi.json').then(m => m.default),
  ml: () => import('@/locales/ml.json').then(m => m.default),
  te: () => import('@/locales/te.json').then(m => m.default),
  bg: () => import('@/locales/bg.json').then(m => m.default),
  cs: () => import('@/locales/cs.json').then(m => m.default),
  sk: () => import('@/locales/sk.json').then(m => m.default),
  ur: () => import('@/locales/ur.json').then(m => m.default),
  pa: () => import('@/locales/pa.json').then(m => m.default),
  zh: () => import('@/locales/zh.json').then(m => m.default),
  yue: () => import('@/locales/yue.json').then(m => m.default),
};

// A language landing page (/hu, /hi, ...) decides the language from its URL,
// so crawlers that send an English browser locale still get that language.
export function pathLanguage() {
  const seg = window.location.pathname.split('/')[1];
  return seg && seg !== 'en' && AVAILABLE_LANGUAGES.includes(seg) ? seg : null;
}

export function loadTranslations(code) {
  const loader = languageLoaders[code];
  return loader ? loader() : Promise.resolve(null);
}

function detectLanguage() {
  const fromPath = pathLanguage();
  if (fromPath) return fromPath;
  const saved = localStorage.getItem('legacylens_lang');
  if (saved && AVAILABLE_LANGUAGES.includes(saved)) return saved;
  const browser = (navigator.language || 'en').split('-')[0];
  return AVAILABLE_LANGUAGES.includes(browser) ? browser : 'en';
}

const I18nContext = createContext(null);

export function I18nProvider({ children, preloaded }) {
  const [language, setLang] = useState(detectLanguage);
  const [loadedTranslations, setLoadedTranslations] = useState(() => ({ en: enTranslations, ...preloaded }));
  const loadingRef = useRef(new Set());

  const isRTL = RTL_LANGUAGES.has(language);

  // Load language file on demand
  useEffect(() => {
    if (loadedTranslations[language] || loadingRef.current.has(language)) return;

    const loader = languageLoaders[language];
    if (!loader) return;

    loadingRef.current.add(language);
    loader().then(translations => {
      setLoadedTranslations(prev => ({ ...prev, [language]: translations }));
      loadingRef.current.delete(language);
    }).catch(err => {
      console.error(`Failed to load language ${language}:`, err);
      loadingRef.current.delete(language);
    });
  }, [language, loadedTranslations]);

  const setLanguage = useCallback((code) => {
    setLang(code);
    localStorage.setItem('legacylens_lang', code);
  }, []);

  // Set dir attribute on <html> for RTL support
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  const t = useCallback((key) => {
    return loadedTranslations[language]?.[key] || loadedTranslations['en']?.[key] || key;
  }, [language, loadedTranslations]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, isRTL, availableLanguages: AVAILABLE_LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used within I18nProvider');
  return ctx;
}
